import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';
import patientApi, { setPatientSession } from '../utils/patientApi';

export default function PatientPortalLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);

  const cleanPhone = (val) => val.replace(/\D/g, '').slice(-10);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const p = cleanPhone(phone);
    if (p.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await patientApi.post('/patient-portal/send-otp', { phone: p });
      setSessionId(res.data.session_id);
      setPhone(p);
      setStep('otp');
      toast.success(`OTP sent to +91 ${p}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      toast.error('Enter the OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await patientApi.post('/patient-portal/verify-otp', {
        phone,
        session_id: sessionId,
        otp,
      });
      setPatientSession(res.data.token, res.data.phone);
      toast.success('Welcome!');
      navigate('/patient-portal/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const res = await patientApi.post('/patient-portal/send-otp', { phone });
      setSessionId(res.data.session_id);
      setOtp('');
      toast.success('OTP resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-600 text-white mb-4 shadow-lg shadow-emerald-200">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Patient Portal</h1>
          <p className="text-slate-500 mt-2 text-sm">Manage your appointments — secure OTP login</p>
        </div>

        <Card className="p-8 border-slate-200 shadow-xl rounded-2xl">
          {step === 'phone' && (
            <form onSubmit={handleSendOTP} className="space-y-5" data-testid="patient-otp-phone-form">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">Mobile number</Label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-300 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100 px-3 py-1.5 transition-colors">
                  <span className="text-slate-500 text-sm flex items-center gap-1">
                    <Phone className="w-4 h-4" /> +91
                  </span>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(cleanPhone(e.target.value))}
                    placeholder="98XXXXXXXX"
                    maxLength={10}
                    className="border-0 focus-visible:ring-0 px-0 text-base"
                    data-testid="patient-portal-phone-input"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-500">We'll send a 6-digit OTP to verify it's you.</p>
              </div>

              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6 text-base font-semibold disabled:opacity-60"
                data-testid="patient-portal-send-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Send OTP <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-5" data-testid="patient-otp-verify-form">
              <div>
                <p className="text-sm text-slate-600">OTP sent to</p>
                <p className="font-semibold text-slate-900">+91 {phone}</p>
                <button
                  type="button"
                  className="text-xs text-emerald-600 hover:underline mt-1"
                  onClick={() => { setStep('phone'); setOtp(''); setSessionId(''); }}
                  data-testid="patient-portal-change-phone-btn"
                >
                  Change number
                </button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="otp" className="text-slate-700 font-medium">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------"
                  maxLength={6}
                  className="text-center text-2xl tracking-[0.5em] font-mono py-6 rounded-xl"
                  data-testid="patient-portal-otp-input"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6 text-base font-semibold"
                data-testid="patient-portal-verify-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Continue'}
              </Button>

              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="w-full text-sm text-slate-500 hover:text-emerald-600"
                data-testid="patient-portal-resend-otp-btn"
              >
                Didn't get it? Resend OTP
              </button>
            </form>
          )}
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          By continuing you agree to clinic terms. Your number is used only for appointment management.
        </p>
      </div>
    </div>
  );
}
